import React, { createContext, useCallback, useContext, useState } from 'react';
import { applyTheme, THEME_STORAGE_KEY } from './tokens';

const ThemeContext = createContext({ mode: 'dark', toggleTheme: () => {}, setMode: () => {} });

function readInitialMode() {
  if (typeof window === 'undefined') return 'dark';
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (_) { /* ignore */ }
  return 'dark';
}

// Reflect the mode onto <html> so CSS (index.css) and native form controls
// (color-scheme) match the JS token palette.
function syncDocument(mode) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
}

export function ThemeProvider({ children }) {
  // Initialise synchronously so the very first render already has the right
  // palette applied (applyTheme mutates the shared token objects).
  const [mode, setModeState] = useState(() => {
    const initial = readInitialMode();
    applyTheme(initial);
    syncDocument(initial);
    return initial;
  });

  const setMode = useCallback((next) => {
    setModeState(() => {
      applyTheme(next);
      syncDocument(next);
      try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      syncDocument(next);
      try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
